import { PageContainer } from '@components/Layout/PageContainer';
import { DataTable } from '@components/DataTable';
import { ResponsiveButton } from '@components/ResponsiveButton';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Add } from '@mui/icons-material';
import { Link, Stack, Typography } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useExpensesQuery } from 'gql/index';
import type { ExpenseFragmentFragment } from 'gql/index';
import { useMoneyFormatter } from '@utils/numberUtils';
import { ExpenseActions } from '../components/Expenses/ExpenseActions';
import { ExpenseDialog } from '../components/Expenses/ExpenseDialog';
import { ExpenseStatusChip } from '../components/CommercialConstructionCalculator/components/ExpenseStatusChip';
import { QuoteDialog } from '../components/BudgetItemTable/components/QuoteDialog';
import dayjs from 'dayjs';

type QuoteEntry = {
  id: number;
  name: string;
  amount: number;
  quoteDate: string;
  attachments?: { id: number; fileName: string; thumbnailStatus?: string }[];
};

type ExpenseRow = {
  id: number;
  name: string;
  costItemLabel: string;
  expendedAmount: number;
  expenseDate: string;
  status: ExpenseFragmentFragment['status'];
  expense: ExpenseFragmentFragment;
  costItemId: number;
  quote?: QuoteEntry | null;
};

export const ProjectExpensesPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { projectId, canManageBudgetAndExpenses } = useCurrentProject();
  const formatMoney = useMoneyFormatter();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseFragmentFragment | undefined>(undefined);
  const [editingCostItemId, setEditingCostItemId] = useState<number | undefined>(undefined);

  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<QuoteEntry | undefined>(undefined);
  const [editingQuoteCostItemId, setEditingQuoteCostItemId] = useState<number | undefined>(undefined);

  const { data: expenses = [], isFetching } = useExpensesQuery(
    { projectId: projectId ?? 0 },
    { select: (d) => d.expenses, enabled: projectId != null }
  );

  const rows: ExpenseRow[] = useMemo(() => {
    return [...expenses]
      .sort((a, b) => dayjs(b.expenseDate).valueOf() - dayjs(a.expenseDate).valueOf())
      .map((expense) => {
        const costItem = expense.entry?.costItem;
        const budgetItem = costItem?.budgetItem;
        const costItemLabel = budgetItem && costItem
          ? `${budgetItem.name} › ${costItem.name}`
          : costItem?.name ?? '—';
        const quote = expense.quote
          ? {
              id: expense.quote.id,
              name: expense.quote.name,
              amount: Number(expense.quote.amount),
              quoteDate: expense.quote.quoteDate,
              attachments: expense.quote.attachments ?? [],
            }
          : null;
        return {
          id: expense.id,
          name: expense.name,
          costItemLabel,
          expendedAmount: Number(expense.expendedAmount),
          expenseDate: expense.expenseDate,
          status: expense.status,
          expense,
          costItemId: costItem?.id ?? 0,
          quote,
        };
      });
  }, [expenses]);

  const handleAddExpense = useCallback(() => {
    setEditingExpense(undefined);
    setEditingCostItemId(undefined);
    setDialogOpen(true);
  }, []);

  const handleEditExpense = useCallback((expense: ExpenseFragmentFragment, costItemId: number) => {
    setEditingExpense(expense);
    setEditingCostItemId(costItemId);
    setDialogOpen(true);
  }, []);

  const handleRowClick = useCallback((params: GridRowParams<ExpenseRow>) => {
    setEditingExpense(params.row.expense);
    setEditingCostItemId(params.row.costItemId);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingExpense(undefined);
    setEditingCostItemId(undefined);
  }, []);

  const handleOpenQuoteDialog = useCallback((quote: QuoteEntry, costItemId: number) => {
    setEditingQuote(quote);
    setEditingQuoteCostItemId(costItemId);
    setQuoteDialogOpen(true);
  }, []);

  const handleQuoteDialogClose = useCallback(() => {
    setQuoteDialogOpen(false);
    setEditingQuote(undefined);
    setEditingQuoteCostItemId(undefined);
  }, []);

  const columns: GridColDef<ExpenseRow>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: formatMessage({ id: 'Name' }),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'costItemLabel',
        headerName: formatMessage({ id: 'Cost item' }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'expendedAmount',
        headerName: formatMessage({ id: 'Amount' }),
        type: 'number',
        width: 120,
        valueFormatter: (value) => formatMoney(Number(value)),
      },
      {
        field: 'expenseDate',
        headerName: formatMessage({ id: 'Date' }),
        width: 120,
        valueFormatter: (value) => (value ? dayjs(String(value)).format('YYYY-MM-DD') : ''),
      },
      {
        field: 'status',
        headerName: formatMessage({ id: 'Status' }),
        width: 120,
        renderCell: ({ row }) => (
          <ExpenseStatusChip status={row.status} size="small" />
        ),
      },
      {
        field: 'quote',
        headerName: formatMessage({ id: 'Quote' }),
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }) => {
          if (!row.quote) return <Typography variant="body2">—</Typography>;
          return (
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenQuoteDialog(row.quote!, row.costItemId);
              }}
            >
              {row.quote.name}
            </Link>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ExpenseActions
              expense={row.expense}
              costItemId={row.costItemId}
              size="small"
              onEdit={handleEditExpense}
            />
          </div>
        ),
      },
    ],
    [formatMessage, formatMoney, handleEditExpense, handleOpenQuoteDialog]
  );

  return (
    <PageContainer title={formatMessage({ id: 'Expenses' })} maxWidth="xl">
      <Stack gap={2}>
        {canManageBudgetAndExpenses && (
        <Stack direction="row" justifyContent="flex-end">
          <ResponsiveButton
            variant="contained"
            icon={<Add />}
            iconPosition="start"
            onClick={handleAddExpense}
          >
            {formatMessage({ id: 'Add expense' })}
          </ResponsiveButton>
        </Stack>
        )}
        <DataTable<ExpenseRow>
          rows={rows}
          columns={columns}
          loading={isFetching}
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: 'expenseDate', sort: 'desc' }],
            },
          }}
          noDataMessage={formatMessage({ id: 'No expenses' })}
        />
      </Stack>
      <ExpenseDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        expense={editingExpense}
        costItemId={editingCostItemId}
      />
      <QuoteDialog
        open={quoteDialogOpen}
        onClose={handleQuoteDialogClose}
        costItemId={editingQuoteCostItemId}
        quote={editingQuote}
      />
    </PageContainer>
  );
};
